import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';

const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;
const BACKEND_BASE_URL = (process.env.REACT_APP_BACKEND_BASEURL || '').replace(/\/+$/, '');

const inFlightTokenRequests = new Map();

function decodeJwtPayload(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function isTokenExpiringSoon(token, bufferMs = TOKEN_REFRESH_BUFFER_MS) {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) {
    return true;
  }
  return payload.exp * 1000 <= Date.now() + bufferMs;
}

export function isBackendRequestUrl(url = '', baseURL = '') {
  if (!BACKEND_BASE_URL) {
    return false;
  }

  const fullUrl = url.startsWith('http') ? url : `${(baseURL || '').replace(/\/+$/, '')}${url}`;
  return fullUrl.startsWith(BACKEND_BASE_URL);
}

async function requestBackendToken(instance, account, forceRefresh) {
  let result = await instance.acquireTokenSilent({
    ...loginRequest,
    account,
    forceRefresh,
  });

  if (!result.idToken) {
    throw new Error('No ID token available for API authentication');
  }

  if (!forceRefresh && isTokenExpiringSoon(result.idToken)) {
    result = await instance.acquireTokenSilent({
      ...loginRequest,
      account,
      forceRefresh: true,
    });

    if (!result.idToken) {
      throw new Error('No ID token available for API authentication');
    }
  }

  return { accessToken: result.idToken };
}

/**
 * Uses the Microsoft ID token from the existing login session to call our backend.
 * This works with the same Azure app registration already used for sign-in —
 * no separate "Expose an API" setup required.
 */
export async function acquireBackendToken(instance, account, { forceRefresh = false } = {}) {
  if (!account) {
    throw new Error('No signed-in account available');
  }

  const requestKey = `${account.homeAccountId}:${forceRefresh ? 'force' : 'silent'}`;
  const pendingRequest = inFlightTokenRequests.get(requestKey);

  if (pendingRequest) {
    try {
      return await pendingRequest;
    } catch {
      // Fall through and attempt a fresh acquisition below.
    }
  }

  const tokenPromise = (async () => {
    try {
      return await requestBackendToken(instance, account, forceRefresh);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({
          ...loginRequest,
          account,
        });
        return null;
      }
      throw error;
    }
  })();

  inFlightTokenRequests.set(requestKey, tokenPromise);

  try {
    return await tokenPromise;
  } finally {
    if (inFlightTokenRequests.get(requestKey) === tokenPromise) {
      inFlightTokenRequests.delete(requestKey);
    }
  }
}

export function withAuthHeader(accessToken, headers = {}) {
  return {
    ...headers,
    Authorization: `Bearer ${accessToken}`,
  };
}

export async function authenticatedFetch(instance, account, url, options = {}, retried = false) {
  const tokenResult = await acquireBackendToken(instance, account, { forceRefresh: retried });

  if (!tokenResult?.accessToken) {
    return new Promise(() => {});
  }

  const headers = withAuthHeader(tokenResult.accessToken, options.headers || {});

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401 && !retried) {
    return authenticatedFetch(instance, account, url, options, true);
  }

  return response;
}

export async function getAuthenticatedAxiosConfig(instance, account, config = {}) {
  const tokenResult = await acquireBackendToken(instance, account);

  if (!tokenResult?.accessToken) {
    throw new Error('Failed to acquire backend authentication token');
  }

  return {
    ...config,
    headers: withAuthHeader(tokenResult.accessToken, config.headers || {}),
  };
}

export async function openAuthenticatedUrl(instance, account, url) {
  // Open synchronously while still inside the user click handler so pop-up blockers allow it.
  const popup = window.open('about:blank', '_blank');

  const tokenResult = await acquireBackendToken(instance, account);
  if (!tokenResult?.accessToken) {
    popup?.close();
    return;
  }

  const jsonUrl = url.includes('?') ? `${url}&format=json` : `${url}?format=json`;

  try {
    let response = await fetch(jsonUrl, {
      headers: { Authorization: `Bearer ${tokenResult.accessToken}` },
    });

    if (response.status === 401) {
      const refreshed = await acquireBackendToken(instance, account, { forceRefresh: true });
      if (refreshed?.accessToken) {
        response = await fetch(jsonUrl, {
          headers: { Authorization: `Bearer ${refreshed.accessToken}` },
        });
      }
    }

    if (!response.ok) {
      popup?.close();
      return;
    }

    const data = await response.json();
    if (data.url && popup) {
      popup.location.href = data.url;
    } else {
      popup?.close();
    }
  } catch (error) {
    console.error('Failed to open authenticated URL:', error);
    popup?.close();
  }
}

export { decodeJwtPayload, isTokenExpiringSoon };
